import { EditorTab } from "./_EditorTab";
import { PageLoader } from "@/zapi-frontend/shared/ui/PageLoader";
import { DiffViewTab } from "./_DiffViewTab";
import { MonacoMarkdownEditor } from "./_MonacoMarkdownEditor";

import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";

import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useDiffStore } from "@/zapi-frontend/stores/diff";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useMemoViewStore } from "@/zapi-frontend/stores/memoView";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { sanitizeChapterHtml } from "@/zapi-frontend/shared/lib/chapterHtml";
import { ChapterBlocks } from "./ChapterBlocks";
import { FigureRefsProvider } from "./FigureRefs/FigureRefsProvider";
import { useModelLedger } from "../ModelLedgerIsland/useModelLedger";
import { fillChapterContent, hasEmptySlots } from "@/zapi-frontend/shared/lib/figureRefs";
import { BlocksSelectionArea } from "./_BlocksSelectionArea";
import { hasPendingDiffs } from "@/zapi-frontend/utils/diff-utils";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { useProjectGenerationState } from "@/zapi-frontend/shared/hooks/useProjectGenerationState";
import { memoType } from "./memoTypography";

export function MemoIsland({ inWidget = false, showTitle = !inWidget }: { inWidget?: boolean; showTitle?: boolean }) {
  const { t } = useTranslation(["projectHome", "common"]);
  const { activeChapter, syncChapterContent } = useChapterStore();
  const { isLabs, role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const { isRawView, isFullWidth, setRawView } = useMemoViewStore();

  const { id } = useParams();
  const projectId = typeof id === "string" ? id : "";
  const chapterId = activeChapter?.id?.toString() || "";

  const { isFetchProjectLoading } = useProjectStore();
  // Chapter content stays read-only until the whole analysis finishes — an edit made
  // mid-run would be overwritten by the generator (INV-2286).
  const { isSetupActive } = useProjectGenerationState(projectId);
  const {
    data: ledger,
    isLoading: isLedgerLoading,
    isError: isLedgerError,
    isFetching: isLedgerFetching,
    refetch: refetchLedger
  } = useModelLedger(projectId);

  const { getDiffEditor } = useDiffStore();
  const htmlContent = getDiffEditor(projectId, chapterId)?.htmlContent;
  const hasDiff = !!htmlContent && hasPendingDiffs(htmlContent);

  useEffect(() => {
    if (!isLabs && isRawView) {
      setRawView(false);
    }
  }, [isLabs, isRawView, setRawView]);

  // The project payload ships chapters without their body; the editor fetches it on mount, but
  // the read-only html/blocks renderers never mount the editor, so they trigger the same fetch here.
  const contentFormat = activeChapter?.activeMemo?.contentFormat;
  const isBlocks = contentFormat === "blocks";
  const isReadOnlyFormat = contentFormat === "html" || isBlocks;
  const [isSyncingContent, setIsSyncingContent] = useState(false);
  useEffect(() => {
    if (!chapterId || !isReadOnlyFormat) return;
    let cancelled = false;
    setIsSyncingContent(true);
    syncChapterContent(chapterId).finally(() => {
      if (!cancelled) setIsSyncingContent(false);
    });
    return () => {
      cancelled = true;
    };
  }, [chapterId, isReadOnlyFormat, syncChapterContent]);
  const isContentLoading =
    (isReadOnlyFormat && isSyncingContent && !activeChapter?.activeMemo?.mdContent) || (isBlocks && isLedgerLoading);
  const isLedgerMissing = isBlocks && isLedgerError && hasEmptySlots(activeChapter?.activeMemo?.mdContent);
  const isPageScrolled = inWidget && (isBlocks || contentFormat === "html");

  return (
    <div
      className={cn(
        "flex w-full min-w-0 mx-auto",
        !isPageScrolled && "h-full",
        (isFullWidth && !inWidget) || isBlocks ? "max-w-none" : "max-w-[720px]"
      )}
    >
      <div
        className={cn(
          "w-full min-w-0 flex flex-col bg-card",
          !isPageScrolled && "h-full",
          !isBlocks && (inWidget ? "px-5 sm:px-6" : "px-4 laptop:px-0")
        )}
      >
        {isFetchProjectLoading && <PageLoader />}

        {!isFetchProjectLoading && isContentLoading && <PageLoader data-testid="memo-content-loading" />}

        {!isFetchProjectLoading && !isContentLoading && isLedgerMissing && (
          <section
            className="m-4 flex flex-col items-center gap-3 rounded border border-innoveraZinc-150 px-6 py-16 laptop:m-10"
            data-testid="memo-figures-error"
          >
            <FigmaTypography variant="heading-3" color="zinc900">
              {t("chapterBlocks.figuresErrorTitle")}
            </FigmaTypography>
            <FigmaTypography variant="body-2" color="zinc500" className="max-w-[380px] text-center">
              {t("chapterBlocks.figuresErrorBody")}
            </FigmaTypography>
            <AppButton
              data-testid="memo-figures-retry"
              appearance="outlined"
              color="zinc150"
              className="mt-1 text-innoveraZinc-900 shadow-none"
              onClick={() => refetchLedger()}
              isLoading={isLedgerFetching}
            >
              {t("chapterBlocks.retry")}
            </AppButton>
          </section>
        )}

        {/* Block chapters scroll as a whole card, the column of content centred inside at the
            design's 725 px (or the full width) with 40 px of padding around it. */}
        {!isFetchProjectLoading && !isContentLoading && !isLedgerMissing && isBlocks && activeChapter?.activeMemo && (
          <div
            className={cn(inWidget ? "p-5 sm:p-6" : "memo-scrollbar min-h-0 flex-1 overflow-x-hidden overflow-y-auto")}
            data-testid="memo-blocks-scroll"
          >
            <BlocksSelectionArea
              className={cn(
                "mx-auto w-full",
                inWidget ? "max-w-[720px]" : "p-4 laptop:p-10",
                !inWidget && !isFullWidth && "laptop:max-w-[805px]"
              )}
              projectId={Number(projectId)}
              chapterId={activeChapter.id}
              contentKey={fillChapterContent(activeChapter.activeMemo.mdContent, ledger?.figures)}
            >
              <FigureRefsProvider figures={ledger?.figures}>
                <ChapterBlocks
                  content={fillChapterContent(activeChapter.activeMemo.mdContent, ledger?.figures)}
                  title={showTitle ? activeChapter.projectTemplateChapter.name : null}
                />
              </FigureRefsProvider>
            </BlocksSelectionArea>
          </div>
        )}

        {!isFetchProjectLoading && !isContentLoading && activeChapter?.activeMemo?.contentFormat === "html" && (
          <div
            className={cn(
              "flex-1 [&_h1]:mb-3 [&_h2]:mt-4 [&_h2]:mb-2 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-innoveraZinc-150 [&_td]:p-2 [&_th]:border [&_th]:border-innoveraZinc-150 [&_th]:p-2 [&_th]:text-left",
              memoType.html,
              inWidget ? "py-6" : "overflow-y-auto py-4"
            )}
            data-testid="memo-html-view"
            dangerouslySetInnerHTML={{ __html: sanitizeChapterHtml(activeChapter.activeMemo.mdContent) }}
          />
        )}

        {!isFetchProjectLoading &&
          activeChapter?.activeMemo?.contentFormat !== "html" &&
          activeChapter?.activeMemo?.contentFormat !== "blocks" && (
          <div className="p-0 m-0 flex-1 flex flex-col relative" data-testid="memo-editor-container">
            {/* TipTap Editor - Always rendered, visibility controlled by CSS */}
            <div className={isRawView ? "hidden" : "block h-full"}>
              <EditorTab
                projectId={projectId}
                isEditable={hasEdit && !isSetupActive}
                className={!hasDiff ? "swap-anim" : "swap-hidden"}
              />
              <DiffViewTab className={hasDiff ? "swap-anim" : "swap-hidden"} />
            </div>

            {/* Monaco Editor - Always rendered, visibility controlled by CSS */}
            <div className={isRawView ? "block h-full" : "hidden"}>
              <div className="p-1 pt-0 flex-1 flex flex-col h-full">
                <MonacoMarkdownEditor projectId={projectId} className="flex-1" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
